import { addDays, dayName, dayOf, diffDays, formatDay, isWeekend } from "../../../utils/weekDates";
import { countWorkingDays } from "../../../utils/leaveDays";
import { sortByAccessor } from "../../../utils/sortRows";
import { fmtHours } from "../timesheets/timesheetsConfig";

export const RANGES = [
  { value: 1, label: "1 week" },
  { value: 2, label: "2 weeks" },
  { value: 4, label: "4 weeks" },
];

// A day is overbooked past this many hours, whatever the person's contract says.
export const DAY_LIMIT = 8;
// The capacity bar turns amber at this share of capacity and red above 100%.
export const WARN_AT = 0.85;

export const TYPE_LABEL = { permanent: "Permanent", contractor: "Contractors", resource: "Resources" };
export const TYPE_COLOR = { permanent: "#1d5fa8", contractor: "#f59e0b", resource: "#8a94a6" };
const TYPE_ORDER = ["permanent", "contractor"];

export const DEFAULT_FILTERS = { search: "", type: "", project: "", department: "", groupBy: "type" };
export const STORAGE_KEY = "scheduleBookingColumns";

export const typeOf = (person) => (person.kind === "ghost" ? "contractor" : "permanent");

// ---- Bookings and leave as plain dates ----
export const dateOf = dayOf;

const num = (v) => (v == null ? null : Number(v));

// Bigint ids arrive from the API as strings, so they are made numbers once here.
// hours_allocated is hours per day times every calendar day, so one day's share is the total over those days.
export const normalizeBooking = (b) => {
  const start = dateOf(b.start_datetime);
  const end = dateOf(b.end_datetime);
  return {
    ...b,
    id: num(b.id),
    employee_id: num(b.employee_id),
    resource_id: num(b.resource_id),
    project_id: num(b.project_id),
    start,
    end,
    perDay: Number(b.hours_allocated || 0) / (diffDays(start, end) + 1),
    title: b.project_name || b.activity_type_name || "Booking",
  };
};

export const normalizeLeave = (r) => ({
  ...r,
  employee_id: num(r.employee_id),
  start: dateOf(r.start_date),
  end: dateOf(r.end_date),
  approved: r.status === "approved",
});

export const workingDays = (start, end) => countWorkingDays(start, end);
export const bookingTotal = (b) => b.perDay * workingDays(b.start, b.end);

// How much of a weekday a person is off: 0, 0.5 or 1, from their approved leave.
export const leaveFraction = (leave, date) => {
  if (isWeekend(date)) return 0;
  const hit = leave.find((l) => l.approved && l.start <= date && date <= l.end);
  if (!hit) return 0;
  const half = (date === hit.start && hit.start_half_day) || (date === hit.end && hit.end_half_day);
  return half ? 0.5 : 1;
};

// First line of defence against double entry: the same range maths for the grid, the list and the booking form.
export const overlaps = (a, b) => a.start <= b.end && b.start <= a.end;

export const hoursOn = (bookings, date) =>
  isWeekend(date) ? 0 : bookings.reduce((s, b) => (b.start <= date && date <= b.end ? s + b.perDay : s), 0);

// What the booking form warns about before saving; `others` are the person's (or resource's) other bookings.
export const bookingWarnings = ({ start, end, perDay, others, leave, resourceOthers = [] }) => {
  if (!start || !end || end < start) return [];
  const out = [];
  const days = [];
  for (let d = start; d <= end; d = addDays(d, 1)) if (!isWeekend(d)) days.push(d);
  const off = days.filter((d) => leaveFraction(leave, d) > 0);
  if (off.length) out.push(`Overlaps approved leave on ${off.length} working day${off.length === 1 ? "" : "s"}.`);
  const over = days.find((d) => hoursOn(others, d) + perDay > DAY_LIMIT);
  if (over) out.push(`Over ${DAY_LIMIT} hours on ${dayName(over)} ${formatDay(over)} (${fmtHours(hoursOn(others, over) + perDay)}).`);
  const clash = resourceOthers.find((o) => overlaps({ start, end }, o));
  if (clash) out.push(`The resource is already booked on ${clash.title}.`);
  return out;
};

// Place items in the fewest lanes where none overlap; longer items go first so they sit on top.
export const assignLanes = (items) => {
  const lanes = [];
  [...items]
    .sort((a, b) => a.start.localeCompare(b.start) || diffDays(b.start, b.end) - diffDays(a.start, a.end))
    .forEach((item) => {
      let lane = lanes.findIndex((end) => end < item.start);
      if (lane === -1) {
        lane = lanes.length;
        lanes.push(item.end);
      } else lanes[lane] = item.end;
      item.lane = lane; // eslint-disable-line no-param-reassign
    });
  return Math.max(1, lanes.length);
};

export const abbreviate = (name) => {
  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  return words.length > 1 ? words.slice(0, 2).map((w) => w[0]).join("").toUpperCase() : (words[0] || "").slice(0, 4);
};

// ---- Rows ----
const matches = (q, values) => values.some((v) => (v || "").toString().toLowerCase().includes(q));
const bookingMatches = (q, b) => matches(q, [b.title, b.project_name, b.company_name, b.activity_type_name, b.notes, b.milestone_name]);

const inView = (item, first, last) => item.start <= last && item.end >= first;

// Resource bookings that overlap another booking of the same resource, with the booking they clash with.
export const resourceClashes = (bookings) => {
  const clashes = new Map();
  bookings.filter((b) => b.resource_id).forEach((a) => {
    const other = bookings.find((b) => b.id !== a.id && b.resource_id === a.resource_id && overlaps(a, b));
    if (other) clashes.set(a.id, other.title);
  });
  return clashes;
};

/**
 * Everything the grid draws: groups of rows, each row with positioned items and its capacity numbers.
 * `dates` is the visible days; people and resources are { id, name, ... } as the schedule API returns them.
 */
export const buildGroups = ({ people, resources, bookings, leave, filters, dates }) => {
  const first = dates[0];
  const last = dates[dates.length - 1];
  const q = filters.search.trim().toLowerCase();
  const clashes = resourceClashes(bookings);

  const rowOf = (kind, who) => {
    const own = kind === "person" ? bookings.filter((b) => b.employee_id === who.id) : bookings.filter((b) => b.resource_id === who.id);
    const myLeave = kind === "person" ? leave.filter((l) => l.employee_id === who.id) : [];
    const nameHit = !!q && matches(q, [who.name]);
    const keep = (b) => (!filters.project || String(b.project_id) === filters.project) && (!q || nameHit || bookingMatches(q, b));
    const shown = own.filter((b) => inView(b, first, last) && keep(b));

    const items = [
      ...shown.map((b) => ({
        ...b,
        kind: "booking",
        clash: clashes.get(b.id) || null,
        leaveClash: kind === "person" && dates.some((d) => b.start <= d && d <= b.end && leaveFraction(myLeave, d) > 0),
      })),
      // Leave belongs to the person, so it hides under a project filter or a search that is not for them.
      ...(!filters.project && (!q || nameHit) ? myLeave.filter((l) => inView(l, first, last)).map((l) => ({ ...l, kind: "leave" })) : []),
    ];
    const laneCount = assignLanes(items);

    const weekly = kind === "person" ? who.weekly_hours || 0 : 0;
    const daily = {};
    let booked = 0;
    let capacity = 0;
    const overDays = new Set();
    dates.forEach((d) => {
      if (isWeekend(d)) return;
      const hours = hoursOn(own, d);
      const off = kind === "person" ? leaveFraction(myLeave, d) : 0;
      daily[d] = hours;
      booked += hours;
      capacity += (weekly / 5) * (1 - off);
      if (hours > DAY_LIMIT || (hours > 0 && off === 1)) overDays.add(d);
    });
    const bookedDays = Object.values(daily).filter((h) => h > 0).length;

    // Milestone due dates of what is booked, one marker per milestone and day.
    const markers = [...new Map(shown.filter((b) => b.milestone_due_date && dates.includes(b.milestone_due_date))
      .map((b) => [`${b.milestone_id}:${b.milestone_due_date}`, { date: b.milestone_due_date, name: b.milestone_name }])).values()];

    return {
      key: `${kind === "person" ? "e" : "r"}${who.id}`,
      kind,
      who,
      items,
      laneCount,
      booked,
      capacity,
      bookedDays,
      markers,
      overDays,
      over: overDays.size > 0 || (capacity > 0 && booked > capacity),
      clashing: items.some((i) => i.clash),
      visible: !q && !filters.project ? true : shown.length > 0 || nameHit,
    };
  };

  const personRows = filters.type === "resource" ? [] : people
    .filter((p) => (!filters.type || typeOf(p) === filters.type) && (!filters.department || (p.department || "") === filters.department))
    .map((p) => rowOf("person", p));
  const resourceRows = filters.type && filters.type !== "resource" ? [] : resources.map((r) => rowOf("resource", r));

  const groupOf = (key, label, color, rows, isResource = false) => {
    const booked = rows.reduce((s, r) => s + r.booked, 0);
    const capacity = rows.reduce((s, r) => s + r.capacity, 0);
    return { key, label, color, rows, isResource, booked, capacity, overbooked: rows.filter((r) => r.over).length, clashing: rows.some((r) => r.clashing) };
  };

  const shownPeople = personRows.filter((r) => r.visible);
  const groups = [];
  if (!filters.groupBy) {
    if (shownPeople.length) groups.push(groupOf("all", null, null, shownPeople));
  } else if (filters.groupBy === "department") {
    const names = [...new Set(shownPeople.map((r) => r.who.department || ""))].sort((a, b) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)));
    names.forEach((n) => groups.push(groupOf(`d:${n}`, n || "No department", "#1d5fa8", shownPeople.filter((r) => (r.who.department || "") === n))));
  } else {
    TYPE_ORDER.forEach((t) => {
      const rows = shownPeople.filter((r) => typeOf(r.who) === t);
      if (rows.length) groups.push(groupOf(t, TYPE_LABEL[t], TYPE_COLOR[t], rows));
    });
  }
  const shownResources = resourceRows.filter((r) => r.visible);
  if (shownResources.length) groups.push(groupOf("resource", TYPE_LABEL.resource, TYPE_COLOR.resource, shownResources, true));
  return groups;
};

// ---- Batch selection ----
// Row keys ticked in the grid -> the ids of the bookings in view that belong to those rows.
export const selectedBookingIds = (groups, rowKeys) =>
  [...new Set(groups.flatMap((g) => g.rows).filter((r) => rowKeys.has(r.key)).flatMap((r) => r.items.filter((i) => i.kind === "booking").map((i) => i.id)))];

export const moveDates = (booking, grabDate, dropDate) => {
  const start = addDays(dropDate, -diffDays(booking.start, grabDate));
  return { start, end: addDays(start, diffDays(booking.start, booking.end)) };
};

// ISO timestamps for a booking on new dates, keeping the time of day it already has.
export const withDates = (booking, start, end) => {
  const at = (date, original) => {
    const t = new Date(original);
    const d = new Date(`${date}T00:00:00`);
    d.setHours(t.getHours(), t.getMinutes(), 0, 0);
    return d.toISOString();
  };
  return { start_datetime: at(start, booking.start_datetime), end_datetime: at(end, booking.end_datetime) };
};

// ---- List view ----
export const LIST_COLUMNS = [
  { key: "who", label: "Who", visible: true },
  { key: "project", label: "Project", visible: true },
  { key: "activity", label: "Activity", visible: true },
  { key: "dates", label: "Dates", visible: true },
  { key: "hours", label: "Hrs/day", visible: true },
  { key: "total", label: "Total", visible: true },
  { key: "client", label: "Client", visible: false },
  { key: "milestone", label: "Milestone", visible: false },
  { key: "notes", label: "Notes", visible: false },
];

const LIST_VALUES = {
  who: (r) => r.who,
  project: (r) => r.title,
  activity: (r) => r.activity_type_name,
  dates: (r) => r.start,
  hours: (r) => r.perDay,
  total: (r) => r.total,
  client: (r) => r.company_name,
  milestone: (r) => r.milestone_name,
  notes: (r) => r.notes,
};

// One row per booking: a booking on a person and a resource is listed once, under the person.
export const listRows = (groups) => {
  const seen = new Set();
  return groups.flatMap((g) => g.rows.flatMap((row) =>
    row.items.filter((i) => i.kind === "booking" && !seen.has(i.id) && seen.add(i.id)).map((b) => ({
      ...b,
      who: row.who.name,
      group: g.label,
      total: bookingTotal(b),
      days: workingDays(b.start, b.end),
      key: String(b.id),
    }))
  ));
};

export const sortList = (rows, sort, customAccessor) => sortByAccessor(rows, sort, sort && (LIST_VALUES[sort.key] || customAccessor?.(sort.key)));

// What "Export to Excel" writes: the table's own values, plus the working days and hours behind them.
export const BOOKING_EXPORT = [
  { label: "Who", value: (r) => r.who },
  { label: "Client", value: (r) => r.company_name },
  { label: "Project", value: (r) => r.project_name || "Internal" },
  { label: "Milestone", value: (r) => r.milestone_name },
  { label: "Activity", value: (r) => r.activity_type_name },
  { label: "Start", value: (r) => r.start },
  { label: "End", value: (r) => r.end },
  { label: "Working days", value: (r) => r.days },
  { label: "Hours per day", value: (r) => Math.round(r.perDay * 100) / 100 },
  { label: "Total hours", value: (r) => Math.round(r.total * 100) / 100 },
  { label: "Notes", value: (r) => r.notes },
];

export const LEAVE_EXPORT = [
  { label: "Person", value: (l) => l.employee_name },
  { label: "Type", value: (l) => l.type_name },
  { label: "Status", value: (l) => (l.approved ? "Approved" : "Pending") },
  { label: "Start", value: (l) => l.start },
  { label: "End", value: (l) => l.end },
  { label: "Working days", value: (l) => workingDays(l.start, l.end) },
];
